/* @flow */

import * as React from 'react';

import type {EntitySummaryRating} from 'nutshell-graphql-types';

import {IconButtonWithConfirmation} from '../button/icon-button/icon-button-with-confirmation';
import {IconOnlyButton} from '../button/icon-only-button/icon-only-button';
import {RetryRegularIcon, TrashRegularIcon} from '../icon';
import {InfoCircleRegularIcon} from '../icon/svg/info-circle-regular-icon';
import {ThumbsDownRegularIcon} from '../icon/svg/thumbs-down-regular-icon';
import {ThumbsUpRegularIcon} from '../icon/svg/thumbs-up-regular-icon';
import {Stack} from '../layout';
import {SavedSettingToast, useManageSaveState} from '../saved-setting-toast';

import {AiBadge} from './ai-badge';

type Props = {|
    userRating?: ?EntitySummaryRating,
    onRate?: (rating: EntitySummaryRating) => Promise<*>,
    isSavingRating?: boolean,
    onRefresh?: () => Promise<*>,
    onDelete?: () => void,
|};

export function Footer(props: Props) {
    const isPositiveRated = props.userRating === 'POSITIVE';
    const isNegativeRated = props.userRating === 'NEGATIVE';

    const {toastState} = useManageSaveState(props.isSavingRating || false, 1000);

    return (
        <Stack justifyContent='space-between' isHorizontal={true}>
            <Stack isHorizontal={true} spacing={4} alignCenter={true}>
                {props.onRefresh && (
                    <IconOnlyButton
                        icon={RetryRegularIcon}
                        onClick={() => {
                            if (props.onRefresh) {
                                props.onRefresh();
                            }
                        }}
                        tooltipText='Refresh summary'
                    />
                )}
                <IconOnlyButton
                    icon={ThumbsUpRegularIcon}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (props.onRate) {
                            props.onRate('POSITIVE');
                        }
                    }}
                    tooltipText='Good summary'
                    isActive={isPositiveRated}
                    isDisabled={!props.onRate || isPositiveRated || props.isSavingRating}
                    variant='purple'
                />
                <IconOnlyButton
                    icon={ThumbsDownRegularIcon}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (props.onRate) {
                            props.onRate('NEGATIVE');
                        }
                    }}
                    tooltipText='Poor summary'
                    isActive={isNegativeRated}
                    isDisabled={!props.onRate || isNegativeRated || props.isSavingRating}
                    variant='purple'
                />
                <IconOnlyButton
                    icon={InfoCircleRegularIcon}
                    // TODO: Add show info popover function
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                    tooltipText='What’s this?'
                    isDisabled={false}
                />
                {props.onDelete && (
                    <IconButtonWithConfirmation
                        onConfirm={() => {
                            if (props.onDelete) {
                                props.onDelete();
                            }
                        }}
                        icon={TrashRegularIcon}
                        tooltipText='Remove summary'
                        question='Are you sure you want to remove this summary?'
                        dialogWidth='240px'
                        noHoverBackground={true}
                        noPadding={true}
                        iconHoverWhenConfirming={true}
                    />
                )}
                <SavedSettingToast state={toastState} autoResetDelay={1500} largerSpinner={true} />
            </Stack>
            <AiBadge />
        </Stack>
    );
}
